import { FC, memo, useLayoutEffect, useMemo, useRef, useState } from "react";

import { ProviderRegistration } from "@asd/customer-portal-api";
import isPropValid from "@emotion/is-prop-valid";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardProps,
  Chip,
  ChipProps,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  lighten,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import moment from "moment-timezone";
import ScheduleSelector from "react-schedule-selector";
import { StyleSheetManager } from "styled-components";

import { PROVIDER_REGISTRATION_TITLE_PROPS, ProviderRegistrationForDisplay } from "~/utils/providerRegistrationHelpers";
import ContactProvider from "../ContactProvider";

interface OverflowChipProps extends ChipProps {
  label: string;
}

const OverflowChip: FC<OverflowChipProps> = ({ label, ...props }) => {
  const [isOverflowed, setIsOverflowed] = useState(false);

  const chipRef = useRef<any>(null);

  useLayoutEffect(() => {
    const labelElement = chipRef.current?.querySelector(".MuiChip-label");

    if (labelElement) {
      setIsOverflowed(labelElement.scrollWidth > labelElement.clientWidth);
    }
  }, [label]);

  return (
    <Tooltip disableHoverListener={!isOverflowed} title={label}>
      <Chip ref={chipRef} label={label} {...props} />
    </Tooltip>
  );
};

interface ProviderCardProps extends CardProps {
  provider: ProviderRegistrationForDisplay;
  isHovered: boolean;
  isSelected: boolean;
  onClick: () => void;
}

const ProviderCard: FC<ProviderCardProps> = ({ provider, isHovered, isSelected, onClick, ...props }) => {
  const theme = useTheme();

  const [showSchedule, setShowSchedule] = useState(false);

  const handleCloseSchedule = () => setShowSchedule(false);

  const isProviderOutOfBounds = (provider.distance ?? 0) > (provider.registration.travelDistance ?? 0);

  const details = useMemo(() => {
    return (
      <Box bgcolor="action.selected" overflow="auto" p={1}>
        {(
          [
            ["contact", "Contact"],
            ["genderDisclosure", "Gender"],
            ["languages", "Language(s)"],
            ["professionalCredentials", "Credentials"],
            ["generalAvailability", "Availability"],
            ["maxHoursWeek", "Max hours per week"],
            ["commuteIndependently", "Commute independently"],
            ["caregiverExperience", "Previous experience"],
            ["genderPreference", "Employer gender preference"],
            ["workWithMinors", "Works with minors"],
            ["considerations", "Considerations"],
          ] as [keyof ProviderRegistration & "contact", string][]
        ).map(([key, label], index) => {
          let value: any = provider.registration[key];

          if (["genderDisclosure", "professionalCredentials", "considerations"].includes(key)) {
            value = value.map(({ value }: { value: string }) => value).join(", ");
          } else if (key === "languages") {
            value = value.map(({ name }: { name: string }) => name).join(", ");
          } else if (key === "genderPreference") {
            value = value.value;
          } else if (typeof value === "boolean") {
            value = value ? "Yes" : "No";
          }

          return (
            <Box
              key={key}
              alignItems="center"
              display="grid"
              gap={1}
              gridTemplateColumns="1fr 1fr"
              p={0.5}
              sx={{
                backgroundColor: index % 2 !== 0 ? "action.focus" : "transparent",
                borderRadius: 1,
              }}
            >
              <Typography>{label}:</Typography>
              <Box justifySelf="end" textAlign="right">
                {key === "contact" && <ContactProvider providerRegistrationId={provider.registration.providerRegistrationId} />}
                {key === "generalAvailability" && (
                  <>
                    <Button onClick={() => setShowSchedule(true)} size="small">
                      View
                    </Button>
                    <Dialog maxWidth="md" onClose={handleCloseSchedule} open={showSchedule} scroll="paper" fullWidth>
                      <DialogTitle sx={{ m: 0, p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography {...PROVIDER_REGISTRATION_TITLE_PROPS}>{provider.registration.name} Availability</Typography>
                      </DialogTitle>
                      <DialogContent dividers>
                        <Box
                          sx={{
                            pointerEvents: "none",

                            "& > div:first-of-type > div:first-of-type > div:first-of-type": {
                              position: "sticky",
                              top: theme.spacing(-2),
                              backgroundColor: "background.paper",
                            },
                          }}
                        >
                          <StyleSheetManager shouldForwardProp={(prop) => isPropValid(prop)}>
                            <ScheduleSelector
                              hourlyChunks={2}
                              hoveredColor={lighten(theme.palette.primary.main, 0.4)}
                              maxTime={24}
                              minTime={0}
                              numDays={7}
                              onChange={() => {}}
                              renderDateLabel={(date) => (
                                <Typography
                                  position="sticky"
                                  sx={{
                                    backgroundColor: "background.paper",
                                  }}
                                  textAlign="center"
                                  top={theme.spacing(-2)}
                                  variant="h6"
                                >
                                  {moment(date).format("dddd")}
                                </Typography>
                              )}
                              renderTimeLabel={(time) => <Typography textAlign="end">{moment(time).format("h:mm a")}</Typography>}
                              selectedColor={theme.palette.primary.main}
                              selection={value}
                              startDate={new Date(1769990400000)} // schedule will show Sunday-Saturday
                              timeFormat="h:mma"
                              unselectedColor={lighten(theme.palette.primary.light, 0.5)}
                            />
                          </StyleSheetManager>
                        </Box>
                      </DialogContent>
                      <DialogActions sx={{ p: 2 }}>
                        <Button onClick={handleCloseSchedule} variant="contained">
                          Close
                        </Button>
                      </DialogActions>
                    </Dialog>
                  </>
                )}
                {!["contact", "generalAvailability"].includes(key) && <Typography sx={{ textTransform: "capitalize" }}>{value}</Typography>}
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  }, [provider, showSchedule, theme]);

  return (
    <Card
      {...props}
      sx={[
        { minHeight: "fit-content", cursor: "pointer" },
        !isSelected && {
          bgcolor: isHovered ? "action.hover" : "action.selected",
        },
      ]}
    >
      <CardContent sx={{ py: 1.5, px: 2 }}>
        <Stack direction="column" onClick={onClick}>
          <Typography
            sx={[
              {
                fontWeight: "bold",
              },
              !!isProviderOutOfBounds && {
                position: "relative",
                color: "text.disabled",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: "50%",
                  left: 0,
                  right: 0,
                  height: "1px",
                  backgroundColor: (theme) => theme.palette.grey[600],
                  zIndex: 1,
                  pointerEvents: "none",
                },
              },
            ]}
          >
            {provider.registration.name}
          </Typography>
          <Typography mt={0.5} sx={{ color: "text.secondary" }}>
            {provider.distance} mile(s) away
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.75} mt={1} width="100%">
            {provider.registration.services?.map(({ key, value }: { key: string; value: string }) => {
              return <OverflowChip key={key} color="default" label={value} size="small" variant="outlined" />;
            })}
          </Stack>
        </Stack>
        <Collapse in={isSelected} timeout="auto">
          <Divider sx={{ my: 1 }} />
          {details}
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default memo(ProviderCard, (prev, next) => {
  return prev.isHovered === next.isHovered && prev.isSelected === next.isSelected;
});
